/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import dataProvider from '../../authentication/dataProvider';
import { useParams } from 'react-router-dom';
import { Loader2, Pencil } from 'lucide-react';
import { services } from '../../services';
import { useToast } from '../../components/ui/use-toast';
import { createAdminNotification } from '../../services/notificationService';
import { useNavigate } from 'react-router-dom';

export default function EditAsset() {
  const { id } = useParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [record, setRecord] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [formState, setFormState] = useState<any>({});
  const [selectedFiles, setSelectedFiles] = useState({});
  const [originalState, setOriginalState] = useState<any>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await dataProvider.getOne('assets', { id });
        if (Object.keys(response.data).length > 0) {
          setLoading(false);
        }
        setRecord(response.data);

        // Ensure image and sideImages are properly initialized
        const initialState = {
          ...response.data,
          image: {
            ...response.data.image,
            sideImages: [...(response.data.image?.sideImages || [])],
          },
        };
        setFormState(initialState);
        setOriginalState(initialState); // Save original for comparison
      } catch (error) {
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Utility: Deep compare two objects (shallow for brevity, use lodash for deep)
  const isObjectEqual = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b);

  // Watch for changes to formState
  useEffect(() => {
    if (!originalState) return;
    setIsDirty(!isObjectEqual(formState, originalState));
  }, [formState, originalState]);

  // Helper: Get only changed fields
  const getChangedFields = (original: any, updated: any) => {
    const changed: any = {};
    Object.keys(updated).forEach((key) => {
      if (typeof updated[key] === 'object' && updated[key] !== null && original[key]) {
        const nested = getChangedFields(original[key], updated[key]);
        if (Object.keys(nested).length > 0) changed[key] = nested;
      } else if (!isObjectEqual(updated[key], original[key])) {
        changed[key] = updated[key];
      }
    });
    return changed;
  };

  if (loading || !record)
    return (
      <div className='flex items-center justify-center h-screen w-full'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
      </div>
    );

  const asset = record;
  const details = asset.AssetDetials?.detials || {};
  const financial = asset.AssetDetials?.financial || {};
  const documents = asset.AssetDetials?.documents || {};

  const handleImageChange = (e: any, index: number) => {
    const file = e.target.files[0];
    if (file) {
      const updatedImages = [...formState.image.sideImages];
      updatedImages[index] = { ...updatedImages[index], file };
      setFormState({ ...formState, image: { ...formState.image, sideImages: updatedImages } });
    }
  };

  const handleDocChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFiles((prev) => ({
        ...prev,
        [key]: file.name,
      }));

      setFormState({
        ...formState,
        AssetDetials: {
          ...formState.AssetDetials,
          documents: {
            ...formState.AssetDetials.documents,
            [key]: file,
          },
        },
      });
    }
  };

  // Add these handlers for financial fields
  const handleFinancialChange = (field: string, value: string) => {
    setFormState({
      ...formState,
      AssetDetials: {
        ...formState.AssetDetials,
        financial: {
          ...formState.AssetDetials?.financial,
          [field]: value,
        },
      },
    });
  };

  // Add this validation function
  const validateChangedFields = (changedPayload: any) => {
    const errors: string[] = [];

    // Helper function to check if a value is empty
    const isEmpty = (value: any) => {
      if (value === null || value === undefined) return true;
      if (typeof value === 'string') return value.trim() === '';
      if (Array.isArray(value)) return value.length === 0;
      if (typeof value === 'object') return Object.keys(value).length === 0;
      return false;
    };

    // Helper function to validate nested objects
    const validateNested = (obj: any, path: string = '') => {
      Object.entries(obj).forEach(([key, value]) => {
        const currentPath = path ? `${path}.${key}` : key;

        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          validateNested(value, currentPath);
        } else if (isEmpty(value)) {
          errors.push(`${currentPath} cannot be empty`);
        }
      });
    };

    validateNested(changedPayload);
    return errors;
  };

  // Update the handleSubmit function
  const handleSubmit = async () => {
    try {
      setIsSaving(true);
      const formData = new FormData();

      // Only send changed fields
      const changedPayload = getChangedFields(originalState, formState);

      // Validate changed fields
      const validationErrors = validateChangedFields(changedPayload);
      if (validationErrors.length > 0) {
        toast({
          description: `Please fill in all required fields`,
          variant: 'destructive',
        });
        return;
      }

      // Create payload with complete objects for changed fields
      const finalPayload: any = {};

      // Handle AssetDetials changes
      if (changedPayload.AssetDetials) {
        finalPayload.AssetDetials = {
          ...originalState.AssetDetials,
          ...changedPayload.AssetDetials,
        };

        // If financial is changed, include complete financial object
        if (changedPayload.AssetDetials.financial) {
          finalPayload.AssetDetials.financial = {
            ...originalState.AssetDetials.financial,
            ...changedPayload.AssetDetials.financial,
          };
        }

        // If detials is changed, include complete detials object
        if (changedPayload.AssetDetials.detials) {
          finalPayload.AssetDetials.detials = {
            ...originalState.AssetDetials.detials,
            ...changedPayload.AssetDetials.detials,
          };
        }

        // If documents is changed, include complete documents object
        if (changedPayload.AssetDetials.documents) {
          finalPayload.AssetDetials.documents = {
            ...originalState.AssetDetials.documents,
            ...changedPayload.AssetDetials.documents,
          };
        }
      }

      // Always include image object with main image
      finalPayload.image = {
        ...originalState.image,
        ...(changedPayload.image || {}),
      };

      // Special handling for sideImages if changed
      if (changedPayload.image?.sideImages) {
        finalPayload.image.sideImages = (formState.image?.sideImages || []).map((img: any) => {
          if (img.file) {
            formData.append('sideImages', img.file);
            return {
              url: img.file.name,
              description: img.description || '',
            };
          }
          return {
            url: img.url,
            description: img.description || '',
          };
        });
      }

      // Main image handling - add to FormData (like side images)
      if (formState.image?.mainImageFile) {
        formData.append('mainImage', formState.image.mainImageFile);
        // Keep original URLs for now, backend will handle the upload
        finalPayload.image.original = formState.image?.original || originalState.image?.original;
        finalPayload.image.compressed =
          formState.image?.compressed || originalState.image?.compressed;
      } else {
        // Always include original main image URLs
        finalPayload.image.original = formState.image?.original || originalState.image?.original;
        finalPayload.image.compressed =
          formState.image?.compressed || originalState.image?.compressed;
      }

      // Add other changed fields
      Object.entries(changedPayload).forEach(([key, value]) => {
        if (!finalPayload[key] && key !== 'AssetDetials' && key !== 'image') {
          finalPayload[key] = value;
        }
      });

      // Convert the final payload to FormData
      Object.entries(finalPayload).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (typeof value === 'object') {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value.toString());
          }
        }
      });

      if (Object.keys(finalPayload).length === 0) {
        toast({ description: 'No changes to save.', variant: 'default' });
        return;
      }

      const response = await services.put(`asset/update/${id}`, formData);

      if (response.status === 200) {
        toast({
          description: 'Property updated successfully',
          variant: 'default',
        });
        createAdminNotification({
          type: 'asset',
          title: 'Asset updated',
          message: `"${record?.name || formState?.name || 'Asset'}" was updated.`,
          priority: 'low',
          assetId: id,
        });
        navigate('/assets');
      }
      if (response.status < 200 || response.status >= 300) {
        throw new Error(response.statusText);
      }

      return response;
    } catch (error) {
      toast({
        description: 'Error updating property!',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className='max-w-4xl mx-auto py-10 space-y-6'>
      <Card>
        <CardContent className='space-y-4 pt-6'>
          <div>
            <Label className='block text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-6'>
              Asset Name
            </Label>
            <Input
              defaultValue={asset.assetName}
              onChange={(e) => setFormState({ ...formState, assetName: e.target.value })}
            />
          </div>

          <div>
            <Label className='block text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-6'>
              Description
            </Label>
            <Textarea
              defaultValue={details.description}
              rows={6}
              onChange={(e) =>
                setFormState({
                  ...formState,
                  AssetDetials: {
                    ...formState.AssetDetials,
                    detials: {
                      ...formState.AssetDetials.detials,
                      description: e.target.value,
                    },
                  },
                })
              }
            />
          </div>

          <div>
            <Label className='block text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-6'>
              Main Image
            </Label>
            <div className='relative group w-fit'>
              <img
                src={
                  formState.image?.mainImageFile
                    ? URL.createObjectURL(formState.image.mainImageFile)
                    : asset.image?.original
                }
                alt='Main'
                className='h-40 mt-2 object-cover rounded-md'
              />
              <label className='absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer'>
                <Pencil className='text-white mr-2' size={16} />
                <span className='text-white text-sm'>Update Image</span>
                <Input
                  type='file'
                  accept='image/*'
                  className='hidden'
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file)
                      setFormState({
                        ...formState,
                        image: { ...formState.image, mainImageFile: file },
                      });
                  }}
                />
              </label>
            </div>
          </div>

          <div>
            <Label className='block text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-6'>
              Side Images
            </Label>
            <div className='space-y-3'>
              {formState.image?.sideImages?.map((img: any, i: number) => (
                <div
                  key={i}
                  className='flex gap-3 items-start border p-2 rounded-md hover:shadow-md transition-shadow'
                >
                  <div className='relative w-32 h-32 flex-shrink-0 flex flex-col items-center'>
                    <img
                      src={img.file ? URL.createObjectURL(img.file) : img.url}
                      alt={img.description || 'Side image'}
                      className='h-32 w-32 object-cover rounded-md'
                    />
                    <label
                      className='mt-2 flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white rounded-md px-2 py-1 cursor-pointer'
                      htmlFor={`sideImageInput-${i}`}
                    >
                      <Pencil className='mr-1' size={12} />
                      <span className='text-xs'>Update</span>
                      <Input
                        id={`sideImageInput-${i}`}
                        type='file'
                        accept='image/*'
                        className='hidden'
                        onChange={(e) => handleImageChange(e, i)}
                      />
                    </label>
                  </div>
                  <div className='flex-1'>
                    <Label htmlFor={`description-${i}`} className='text-xs font-medium mb-1'>
                      Image Description
                    </Label>
                    <Textarea
                      id={`description-${i}`}
                      value={img.description || ''}
                      placeholder='Enter a description for this image'
                      rows={3}
                      className='resize-none text-sm'
                      onChange={(e) => {
                        const updatedImages = [...formState.image.sideImages];
                        updatedImages[i] = { ...updatedImages[i], description: e.target.value };
                        setFormState({
                          ...formState,
                          image: { ...formState.image, sideImages: updatedImages },
                        });
                      }}
                    />
                    <p className='text-xs text-gray-500 mt-1'>
                      Provide details about what this image shows
                    </p>
                  </div>
                </div>
              ))}

              <div className='flex items-center justify-center border-2 border-dashed border-gray-300 rounded-md p-4 hover:bg-gray-50 transition-colors cursor-pointer'>
                <label
                  htmlFor='add-side-image'
                  className='flex flex-col items-center cursor-pointer w-full'
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-8 w-8 text-gray-400'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M12 4v16m8-8H4'
                    />
                  </svg>
                  <span className='text-sm font-medium text-gray-600 mt-2'>Add New Image</span>
                  <p className='text-xs text-gray-500 mt-1'>Click to upload a side image</p>
                </label>
                <Input
                  id='add-side-image'
                  type='file'
                  accept='image/*'
                  className='hidden'
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    if (file) {
                      const updatedImages = [
                        ...(formState.image?.sideImages || []),
                        { file, description: '' },
                      ];
                      setFormState({
                        ...formState,
                        image: { ...formState.image, sideImages: updatedImages },
                      });
                    }
                  }}
                />
              </div>
            </div>
          </div>

          <div>
            <Label className='block text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-6'>
              Facilities
            </Label>
            <div className='space-y-2'>
              {formState.AssetDetials?.detials?.facilities?.map((fac: any, i: number) => (
                <div key={i} className='flex items-center gap-2'>
                  <Input
                    placeholder='Facility Name'
                    value={fac.name}
                    onChange={(e) => {
                      const updated = [...formState.AssetDetials.detials.facilities];
                      updated[i].name = e.target.value;
                      setFormState({
                        ...formState,
                        AssetDetials: {
                          ...formState.AssetDetials,
                          detials: { ...formState.AssetDetials.detials, facilities: updated },
                        },
                      });
                    }}
                  />
                  <button
                    className='flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-md px-2 py-1'
                    onClick={() => {
                      const updated = [...formState.AssetDetials.detials.facilities];
                      updated.splice(i, 1);
                      setFormState({
                        ...formState,
                        AssetDetials: {
                          ...formState.AssetDetials,
                          detials: { ...formState.AssetDetials.detials, facilities: updated },
                        },
                      });
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-3.5 w-3.5 mr-1'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                      />
                    </svg>
                    <span className='text-xs'>Delete</span>
                  </button>
                  {/* <Input
                    placeholder='Icon URL'
                    value={fac.icon}
                    onChange={(e) => {
                      const updated = [...formState.AssetDetials.detials.facilities];
                      updated[i].icon = e.target.value;
                      setFormState({
                        ...formState,
                        AssetDetials: {
                          ...formState.AssetDetials,
                          detials: { ...formState.AssetDetials.detials, facilities: updated },
                        },
                      });
                    }}
                  />
                  {fac.icon && <img src={fac.icon} alt='icon' className='h-8 w-8' />} */}
                </div>
              ))}
              <Button
                type='button'
                variant='outline'
                onClick={() => {
                  const newFacility = { name: '', icon: '' };
                  const existing = formState.AssetDetials?.detials?.facilities || [];
                  setFormState({
                    ...formState,
                    AssetDetials: {
                      ...formState.AssetDetials,
                      detials: {
                        ...formState.AssetDetials.detials,
                        facilities: [...existing, newFacility],
                      },
                    },
                  });
                }}
              >
                + Add Facility
              </Button>
            </div>
          </div>

          <div>
            <Label className='block text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-6'>
              Financials
            </Label>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <Label>Total Share</Label>
                <Input
                  required
                  defaultValue={financial.totalShare}
                  placeholder='Total Share'
                  onChange={(e) => handleFinancialChange('totalShare', e.target.value)}
                />
              </div>
              <div>
                <Label>Total Token Value</Label>
                <Input
                  required
                  defaultValue={financial.totalTokenValue}
                  placeholder='Total Token Value'
                  onChange={(e) => handleFinancialChange('totalTokenValue', e.target.value)}
                />
              </div>
              <div>
                <Label>Total Rental Income</Label>
                <Input
                  required
                  defaultValue={financial.totalRentalIncome}
                  placeholder='Total Rental Income'
                  onChange={(e) => handleFinancialChange('totalRentalIncome', e.target.value)}
                />
              </div>
              <div>
                <Label>Asset Appreciation</Label>
                <Input
                  required
                  defaultValue={financial.totalAssetAppreciation}
                  placeholder='Asset Appreciation'
                  onChange={(e) => handleFinancialChange('totalAssetAppreciation', e.target.value)}
                />
              </div>
              <div>
                <Label>Investment Return %</Label>
                <Input
                  required
                  defaultValue={financial.investmentReturn}
                  placeholder='Investment Return %'
                  onChange={(e) => handleFinancialChange('investmentReturn', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div>
            <Label className='block text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-6'>
              Documents
            </Label>
            <div className='grid gap-3'>
              {Object.entries(documents).map(([key, value]: [string, any]) => (
                <div key={key} className='border rounded-md p-3 hover:shadow-sm transition-shadow'>
                  <span className='text-sm font-medium capitalize block mb-2'>{key}</span>
                  <div className='flex items-center gap-2'>
                    <div className='flex-1 flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-md px-3 py-2'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        className='h-5 w-5 text-gray-500'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                        />
                      </svg>
                      <span className='truncate text-sm flex-1'>
                        {selectedFiles[key] || value?.split('/').pop() || 'No file selected'}
                      </span>
                    </div>
                    <label
                      htmlFor={`${key}-file`}
                      className='flex items-center justify-center 
                      bg-primary text-white 
                      dark:bg-white dark:text-black 
                      hover:opacity-90 
                      rounded-md px-3 py-2 cursor-pointer transition'
                    >
                      <Pencil className='mr-1' size={14} />
                      <span className='text-sm'>Update</span>
                      <Input
                        type='file'
                        id={`${key}-file`}
                        className='hidden'
                        onChange={(e) => handleDocChange(e, key)}
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className='block text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-6'>
              Map URL
            </Label>
            <Input
              required
              defaultValue={asset.locationUrl}
              onChange={(e) => setFormState({ ...formState, locationUrl: e.target.value })}
            />
          </div>

          <div>
            <Label className='block text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-6'>
              Youtube URL
            </Label>
            <Input
              required
              defaultValue={asset.socialYtUrl}
              onChange={(e) => setFormState({ ...formState, socialYtUrl: e.target.value })}
            />
          </div>

          <Button className='mt-4' onClick={handleSubmit} disabled={!isDirty || isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
